import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { auth, db } from '../../lib/firebase';
import {
  onAuthStateChanged,
  User as FirebaseUser
} from 'firebase/auth';
import {
  doc,
  setDoc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp
} from 'firebase/firestore';
import { supabase } from '../../lib/supabaseClient';
import { notificationService } from '../../services/notificationService';
import logo from '../../assets/images/logo.png';

const MAX_SHOPS_PER_USER = 2;

// ============================================================
// NIGERIAN STATES (36 + FCT)
// ============================================================
const NIGERIAN_STATES = [
  'Abia', 'Adamawa', 'Akwa Ibom', 'Anambra', 'Bauchi', 'Bayelsa', 'Benue',
  'Borno', 'Cross River', 'Delta', 'Ebonyi', 'Edo', 'Ekiti', 'Enugu',
  'FCT - Abuja', 'Gombe', 'Imo', 'Jigawa', 'Kaduna', 'Kano', 'Katsina',
  'Kebbi', 'Kogi', 'Kwara', 'Lagos', 'Nasarawa', 'Niger', 'Ogun', 'Ondo',
  'Osun', 'Oyo', 'Plateau', 'Rivers', 'Sokoto', 'Taraba', 'Yobe', 'Zamfara'
];

const MAX_HASHTAGS = 5;

// ============================================================
// TYPES
// ============================================================
interface VendorFormData {
  shopName: string;
  bio: string;
  location: string;
  contactPhone: string;
  businessEmail: string;
  website: string;
  instagramHandle: string;
  facebookPage: string;
  twitterHandle: string;
  businessHours: string;
  returnPolicy: string;
  shippingPolicy: string;
}

interface ShopCountInfo {
  currentCount: number;
  maxAllowed: number;
}

interface CategoryGroup {
  id: number;
  name: string;
}

type StepKey =
  | 'shopName'
  | 'bio'
  | 'category'
  | 'hashtags'
  | 'location'
  | 'contactPhone'
  | 'businessEmail'
  | 'businessHours'
  | 'website'
  | 'instagramHandle'
  | 'facebookPage'
  | 'twitterHandle'
  | 'returnPolicy'
  | 'shippingPolicy'
  | 'images'
  | 'review';

interface StepConfig {
  key: StepKey;
  title: string;
  subtitle?: string;
  required: boolean;
}

// ============================================================
// NOTIFICATION
// ============================================================
const sendNewShopNotification = async (
  shopName: string,
  shopId: string,
  ownerName: string
) => {
  try {
    const { data: users, error: usersError } = await supabase
      .from('users')
      .select('firebase_uid, email, name')
      .not('firebase_uid', 'is', null);

    if (usersError || !users || users.length === 0) return;

    const firebaseUids = users.map(u => u.firebase_uid).filter(Boolean);
    const emails = users.map(u => u.email).filter(Boolean);

    const notificationTitle = `🛍️ New Shop Alert: ${shopName}`;
    const notificationBody = `${shopName} is now live on Campus GOSTOREz! Shop owner: ${ownerName}. Check out their amazing products! 🎉`;
    const shortNotificationBody = `${shopName} is now live on GOSTOREz!`;

    const notificationData: any = {
      title: notificationTitle,
      body: notificationBody,
      notification_type: 'vendor',
      redirect_url: `/vendor/${shopId}`,
      data: {
        shopId,
        shopName,
        ownerName,
        type: 'new_shop_launch',
        timestamp: new Date().toISOString()
      }
    };

    if (firebaseUids.length > 0) notificationData.target_user_ids = firebaseUids;
    if (emails.length > 0) notificationData.email_list = emails;

    const response = await notificationService.sendNotification(notificationData);

    if (response && response.success) {
      const inAppNotifications = users.map(user => ({
        user_id: user.firebase_uid,
        title: notificationTitle,
        message: shortNotificationBody,
        type: 'new_shop',
        data: {
          shopId,
          shopName,
          ownerName,
          redirect_url: `/vendor/${shopId}`
        },
        is_read: false,
        created_at: new Date().toISOString()
      }));

      const batchSize = 50;
      for (let i = 0; i < inAppNotifications.length; i += batchSize) {
        const batch = inAppNotifications.slice(i, i + batchSize);
        await supabase.from('user_notifications').insert(batch);
      }
    }
  } catch (error) {
    console.error('❌ sendNewShopNotification failed:', error);
  }
};

// ============================================================
// HELPERS
// ============================================================
const blockDigits = (value: string) => value.replace(/[0-9]/g, '');

// Safely coerce a string to null when empty so DB stores NULL, not ''
const nn = (v: string | undefined | null): string | null => {
  const t = (v ?? '').trim();
  return t.length === 0 ? null : t;
};

// Sanitize a single hashtag input: strip leading #'s and any spaces
const sanitizeHashtagInput = (raw: string): string =>
  raw.replace(/#/g, '').replace(/\s+/g, '');

// Build the hashtags string in the same shape OpenMarket uses:
//   #tag1#tag2#tag3
// Empty input → null (so DB stores NULL).
const buildHashtagsString = (tags: string[]): string | null => {
  const cleaned = tags
    .map(t => sanitizeHashtagInput(t).trim())
    .filter(t => t.length > 0);
  if (cleaned.length === 0) return null;
  return cleaned.map(t => `#${t}`).join('');
};

// ============================================================
// COMPONENT
// ============================================================
const VendorOnboarding: React.FC = () => {
  const [formData, setFormData] = useState<VendorFormData>({
    shopName: '',
    bio: '',
    location: '',
    contactPhone: '',
    businessEmail: '',
    website: '',
    instagramHandle: '',
    facebookPage: '',
    twitterHandle: '',
    businessHours: '',
    returnPolicy: 'Payment on delivery',
    shippingPolicy:
      'Receive our goods or services at our physical shop location. Contact us for more information. Thank you!'
  });

  const [addressInput, setAddressInput] = useState('');
  const [selectedState, setSelectedState] = useState('');

  const [profileImage, setProfileImage] = useState<string>('');
  const [coverImage, setCoverImage] = useState<string>('');
  const [imagePreview, setImagePreview] = useState<string>('');
  const [coverPreview, setCoverPreview] = useState<string>('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [userShopCount, setUserShopCount] = useState<ShopCountInfo>({
    currentCount: 0,
    maxAllowed: MAX_SHOPS_PER_USER
  });
  const [checkingShopCount, setCheckingShopCount] = useState(true);
  const [userName, setUserName] = useState<string>('');

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [showCongrats, setShowCongrats] = useState(false);
  const [createdShopId, setCreatedShopId] = useState('');

  // ---- Category group state ----
  const [categories, setCategories] = useState<CategoryGroup[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoriesError, setCategoriesError] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);

  // ---- Hashtags state (max 5, same as OpenMarket) ----
  const [hashtags, setHashtags] = useState<string[]>(['', '', '', '', '']);

  const navigate = useNavigate();

  const steps: StepConfig[] = [
    { key: 'shopName', title: 'What is your shop called?', subtitle: 'Choose a unique name that represents your brand', required: true },
    { key: 'bio', title: 'Describe your shop', subtitle: 'Tell buyers what you sell and what makes you unique', required: true },
    { key: 'category', title: 'Best match category', subtitle: 'Select an option that best fits your business', required: true },
    { key: 'hashtags', title: 'Set hashtags', subtitle: 'Words that should display your product when user types it. Optional — up to 5.', required: false },
    { key: 'location', title: 'Where is your business located?', subtitle: 'Pick your state and enter your specific address', required: true },
    { key: 'contactPhone', title: 'Contact phone number', subtitle: 'How should buyers reach you?', required: true },
    { key: 'businessEmail', title: 'Business email', subtitle: 'Pre-filled from your account — edit if needed', required: false },
    { key: 'businessHours', title: 'Business hours', subtitle: 'When are you open?', required: false },
    { key: 'website', title: 'Website', subtitle: 'Optional — leave blank if you do not have one', required: false },
    { key: 'instagramHandle', title: 'Instagram handle', subtitle: 'Optional', required: false },
    { key: 'facebookPage', title: 'Facebook page', subtitle: 'Optional', required: false },
    { key: 'twitterHandle', title: 'Twitter handle', subtitle: 'Optional', required: false },
    { key: 'returnPolicy', title: 'How do you intend to receive payment?', subtitle: 'Numbers are not allowed here', required: true },
    { key: 'shippingPolicy', title: 'How will the buyer get the goods or services?', subtitle: 'Numbers are not allowed here', required: true },
    { key: 'images', title: 'Shop images', subtitle: 'Add a logo (optional) and cover image (optional)', required: false },
    { key: 'review', title: 'Review & create', subtitle: 'Check your details before submitting', required: true }
  ];

  const currentStep = steps[currentStepIndex];
  const totalSteps = steps.length;
  const progressPercent = Math.round(((currentStepIndex + 1) / totalSteps) * 100);

  // ---- Auth + prefill ----
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        await checkUserShopCount(user.uid);
        await fetchUserName(user.uid);
        setFormData(prev => ({
          ...prev,
          businessEmail: prev.businessEmail || user.email || '',
          contactPhone: prev.contactPhone || user.phoneNumber || ''
        }));
      } else {
        setCheckingShopCount(false);
      }
    });

    return () => unsubscribe();
  }, []);

  // ---- Fetch categories when category step is reached ----
  useEffect(() => {
    if (currentStep.key === 'category' && categories.length === 0 && !categoriesLoading) {
      fetchCategories();
    }
  }, [currentStep.key]);

  const fetchCategories = async () => {
    setCategoriesLoading(true);
    setCategoriesError('');
    try {
      // First attempt: fetch id and name
      const { data, error } = await supabase
        .from('vendimgcat')
        .select('id, name')
        .order('name', { ascending: true });

      if (error) throw error;

      if (data && data.length > 0) {
        setCategories(data as CategoryGroup[]);
      } else {
        // Fallback: try again fetching only id and name
        const { data: fallbackData, error: fallbackError } = await supabase
          .from('vendimgcat')
          .select('id, name')
          .order('name', { ascending: true });

        if (fallbackError) throw fallbackError;
        setCategories((fallbackData || []).map((c: any) => ({ id: c.id, name: c.name })));
      }
    } catch (err: any) {
      // Fallback: try again fetching only id and name
      try {
        const { data: fallbackData, error: fallbackError } = await supabase
          .from('vendimgcat')
          .select('id, name')
          .order('name', { ascending: true });

        if (fallbackError) throw fallbackError;
        setCategories((fallbackData || []).map((c: any) => ({ id: c.id, name: c.name })));
      } catch (fallbackErr: any) {
        setCategoriesError('Failed to load categories. Please try again.');
        console.error('❌ Failed to fetch categories:', fallbackErr);
      }
    } finally {
      setCategoriesLoading(false);
    }
  };

  const fetchUserName = async (userId: string) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setUserName(userData.name || currentUser?.displayName || 'A new vendor');
        if (!formData.contactPhone && userData.phone) {
          setFormData(prev => ({ ...prev, contactPhone: userData.phone }));
        }
        if (!formData.businessEmail && userData.email) {
          setFormData(prev => ({ ...prev, businessEmail: userData.email }));
        }
      } else {
        setUserName(currentUser?.displayName || 'A new vendor');
      }
    } catch {
      setUserName(currentUser?.displayName || 'A new vendor');
    }
  };

  const checkUserShopCount = async (userId: string) => {
    try {
      const shopsQuery = query(
        collection(db, 'vendors'),
        where('userId', '==', userId)
      );
      const querySnapshot = await getDocs(shopsQuery);
      setUserShopCount({
        currentCount: querySnapshot.size,
        maxAllowed: MAX_SHOPS_PER_USER
      });
    } catch {
      setError('Failed to check your existing shops');
    } finally {
      setCheckingShopCount(false);
    }
  };

  // ---- Input handlers ----
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const fileToDataURL = (file: File): Promise<string> =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.readAsDataURL(file);
    });

  const handleImageSelect = async (
    e: React.ChangeEvent<HTMLInputElement>,
    type: 'profile' | 'cover'
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataURL = await fileToDataURL(file);
      if (type === 'profile') {
        setImagePreview(dataURL);
        setProfileImage(dataURL);
      } else {
        setCoverPreview(dataURL);
        setCoverImage(dataURL);
      }
      setError('');
    } catch {
      setError('Failed to process image');
    }
  };

  const triggerFileInput = (id: string) => {
    const el = document.getElementById(id) as HTMLInputElement | null;
    if (el) el.click();
  };

  const generateShopId = (shopName: string, userId: string): string => {
    const timestamp = Date.now();
    const cleanShopName = shopName
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    return `${userId}_${cleanShopName}_${timestamp}`;
  };

  const checkShopNameExists = async (shopName: string, userId: string): Promise<boolean> => {
    try {
      const shopsQuery = query(
        collection(db, 'vendors'),
        where('userId', '==', userId),
        where('shopName', '==', shopName.trim())
      );
      const querySnapshot = await getDocs(shopsQuery);
      return !querySnapshot.empty;
    } catch {
      return false;
    }
  };

  // ---- Hashtag handler (mirror of OpenMarket) ----
  // Strips '#' and whitespace so the input never accepts them.
  const handleHashtagChange = (idx: number, val: string) => {
    setHashtags(prev => {
      const next = [...prev];
      next[idx] = sanitizeHashtagInput(val);
      return next;
    });
  };

  // ---- Step validation ----
  const isStepValid = (key: StepKey): boolean => {
    switch (key) {
      case 'shopName':
        return formData.shopName.trim().length >= 2;
      case 'bio':
        return formData.bio.trim().length >= 5;
      case 'category':
        return selectedCategoryId !== null;
      case 'hashtags':
        // Optional step — always allowed to proceed
        return true;
      case 'location':
        return !!selectedState && addressInput.trim().length >= 2;
      case 'contactPhone':
        return formData.contactPhone.trim().length >= 6;
      case 'returnPolicy':
        return formData.returnPolicy.trim().length >= 5;
      case 'shippingPolicy':
        return formData.shippingPolicy.trim().length >= 5;
      case 'images':
        return true; // logo now optional
      case 'review':
        return true;
      default:
        return true;
    }
  };

  const canProceed = isStepValid(currentStep.key);

  const canSubmit = (): boolean => {
    return (
      formData.shopName.trim().length >= 2 &&
      formData.bio.trim().length >= 5 &&
      selectedCategoryId !== null &&
      !!selectedState &&
      addressInput.trim().length >= 2 &&
      formData.contactPhone.trim().length >= 6 &&
      formData.returnPolicy.trim().length >= 5 &&
      formData.shippingPolicy.trim().length >= 5
    );
  };

  const goNext = () => {
    if (!canProceed) return;
    if (currentStep.key === 'location') {
      setFormData(prev => ({
        ...prev,
        location: `${addressInput.trim()}, ${selectedState}`
      }));
    }
    setError('');
    setCurrentStepIndex(i => Math.min(i + 1, totalSteps - 1));
  };

  const goBack = () => {
    setError('');
    setCurrentStepIndex(i => Math.max(i - 1, 0));
  };

  const skip = () => {
    if (currentStep.required) return;
    setError('');
    setCurrentStepIndex(i => Math.min(i + 1, totalSteps - 1));
  };

  // ============================================================
  // SUBMIT  — hardened, all-fields, guaranteed to release loading
  // ============================================================
  const handleSubmit = async () => {
    if (loading) return;
    setError('');
    setLoading(true);

    try {
      if (!currentUser) throw new Error('You must be logged in to create a vendor profile.');

      if (userShopCount.currentCount >= userShopCount.maxAllowed) {
        throw new Error(`You have reached the maximum limit of ${userShopCount.maxAllowed} shops.`);
      }

      // Final required checks
      if (formData.shopName.trim().length < 2) throw new Error('Shop name is required');
      if (formData.bio.trim().length < 5) throw new Error('Shop description is required');
      if (selectedCategoryId === null) throw new Error('Category group is required');
      if (!selectedState || addressInput.trim().length < 2) throw new Error('Business location is required');
      if (formData.contactPhone.trim().length < 6) throw new Error('Contact phone is required');
      if (formData.returnPolicy.trim().length < 5) throw new Error('Payment method is required');
      if (formData.shippingPolicy.trim().length < 5) throw new Error('Delivery method is required');

      const userId = currentUser.uid;
      const fullLocation = `${addressInput.trim()}, ${selectedState}`;

      // Build the hashtags string once — used in both Firebase and Supabase mirrors
      const hashtagsString = buildHashtagsString(hashtags);

      const exists = await checkShopNameExists(formData.shopName, userId);
      if (exists) throw new Error('You already have a shop with this name. Please choose a different name.');

      const shopId = generateShopId(formData.shopName, userId);

      // ---------------- FIREBASE (source of truth for app) ----------------
      const vendorData = {
        userId,
        shopId,
        shopName: formData.shopName.trim(),
        bio: formData.bio.trim(),
        vendcat: selectedCategoryId,
        hashtags: hashtagsString,
        location: fullLocation,
        contactPhone: formData.contactPhone.trim(),
        businessEmail: formData.businessEmail.trim(),
        website: nn(formData.website),
        instagramHandle: nn(formData.instagramHandle),
        facebookPage: nn(formData.facebookPage),
        twitterHandle: nn(formData.twitterHandle),
        businessHours: nn(formData.businessHours),
        returnPolicy: formData.returnPolicy.trim(),
        shippingPolicy: formData.shippingPolicy.trim(),
        profileImage: profileImage || null,
        coverImage: coverImage || null,
        followersCount: 0,
        likesCount: 0,
        salesCount: 0,
        totalRevenue: 0,
        isActive: true,
        onboardingCompleted: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await setDoc(doc(db, 'vendors', shopId), vendorData);

      // ---------------- SUPABASE (mirror for marketplace + notifications) ----------------
      // Every column the table has is written. Empty fields become NULL.
      const supabaseRow = {
        user_id: userId,
        vendor_id: shopId,
        shop_name: formData.shopName.trim(),
        bio: nn(formData.bio),
        vendcat: selectedCategoryId,
        hashtags: hashtagsString,
        location: fullLocation,
        contact_phone: nn(formData.contactPhone),
        business_email: nn(formData.businessEmail),
        website: nn(formData.website),
        instagram_handle: nn(formData.instagramHandle),
        facebook_page: nn(formData.facebookPage),
        twitter_handle: nn(formData.twitterHandle),
        business_hours: nn(formData.businessHours),
        return_policy: nn(formData.returnPolicy),
        shipping_policy: nn(formData.shippingPolicy),
        profile_image: profileImage || null,
        cover_image: coverImage || null,
        followers_count: 0,
        likes_count: 0,
        is_active: true,
        onboarding_completed: true,
        sales_count: 0,
        total_revenue: 0,
        total_products: 0,
        total_sales: 0,
        average_rating: 0,
        rating_count: 0,
        room_count: 0,
        rooms: [],
        pending_balance: 0,
        available_balance: 0,
        balance: 0,
        total_earnings: 0,
        total_withdrawals: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const { error: sbError } = await supabase
        .from('vendor_profiles')
        .insert([supabaseRow]);

      if (sbError) {
        // Firebase write succeeded; mirror failed. Surface it but do not block the user
        // from seeing their success screen.
        console.error('❌ Supabase vendor_profiles insert failed:', sbError);
        // Common cause: duplicate vendor_id — clean it up so retry works next time
        if (sbError.code === '23505') {
          console.warn('Duplicate vendor_id detected — you may retry.');
        }
        setError(
          'Your shop was created in the app, but the marketplace mirror failed to sync. ' +
          'Please contact support so we can complete the sync. Details: ' + sbError.message
        );
      }

      // Fire-and-forget notifications
      sendNewShopNotification(
        formData.shopName.trim(),
        shopId,
        userName || currentUser.displayName || 'A new vendor'
      );

      setCreatedShopId(shopId);
      setShowCongrats(true);
    } catch (err: any) {
      console.error('❌ Shop creation failed:', err);
      const msg = err?.message || 'An unexpected error occurred. Please try again.';
      if (err?.code === 'permission-denied') {
        setError('Database permission denied. Please check your Firestore security rules.');
      } else if (msg.toLowerCase().includes('quota')) {
        setError('Storage quota exceeded. Please try with smaller images.');
      } else {
        setError(msg);
      }
    } finally {
      // CRITICAL: always release the loading flag so the button never hangs
      setLoading(false);
    }
  };

  // ============================================================
  // LOADING / GUARD SCREENS
  // ============================================================
  if (checkingShopCount) {
    return (
      <div className="min-h-screen bg-[#F4F1E8] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#9B4819] mx-auto"></div>
          <p className="mt-4 text-gray-600">Checking your account...</p>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-[#F4F1E8] flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center">
            <img src={logo} alt="Campus GOSTOREz" className="h-32 w-auto" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-bold text-[#9B4819]">
            Become a Vendor
          </h2>
        </div>
        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-6 shadow-xl rounded-2xl sm:px-10 text-center border border-black/5">
            <div className="mb-6">
              <div className="w-20 h-20 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">🔒</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Authentication Required
              </h3>
              <p className="text-gray-600 mb-6 text-sm">
                You need to be logged in to create a vendor profile and start selling on Campus GOSTOREz.
              </p>
            </div>
            <div className="space-y-4">
              <Link
                to="/signin"
                className="w-full inline-flex justify-center py-3 px-4 text-sm font-medium rounded-xl text-white bg-[#9B4819] hover:bg-[#7a3914] transition"
              >
                Sign In to Continue
              </Link>
              <p className="text-sm text-gray-600">
                Don't have an account?{' '}
                <Link to="/signup" className="font-medium text-[#9B4819] hover:text-[#7a3914]">
                  Create one here
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (userShopCount.currentCount >= userShopCount.maxAllowed) {
    return (
      <div className="min-h-screen bg-[#F4F1E8] flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center">
            <img src={logo} alt="Campus GOSTOREz" className="h-32 w-auto" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-bold text-[#9B4819]">
            Shop Limit Reached
          </h2>
        </div>
        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-6 shadow-xl rounded-2xl sm:px-10 text-center border border-black/5">
            <div className="mb-6">
              <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">🏪</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Maximum Shops Created
              </h3>
              <p className="text-gray-600 mb-4 text-sm">
                You have reached the maximum limit of {userShopCount.maxAllowed} shops per account.
              </p>
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-sm text-gray-600">
                  <strong>Current Shops:</strong> {userShopCount.currentCount}/{userShopCount.maxAllowed}
                </p>
              </div>
            </div>
            <div className="space-y-4">
              <Link
                to="/vendor/dashboard"
                className="w-full inline-flex justify-center py-3 px-4 text-sm font-medium rounded-xl text-white bg-[#9B4819] hover:bg-[#7a3914] transition"
              >
                Go to Vendor Dashboard
              </Link>
              <p className="text-sm text-gray-600">
                Need more shops?{' '}
                <a
                  href="mailto:support@campusgostorez.com?subject=Request for Additional Shops"
                  className="font-medium text-[#9B4819] hover:text-[#7a3914]"
                >
                  Contact Support
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // CONGRATULATIONS SCREEN
  // ============================================================
  if (showCongrats) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#F4F1E8] to-[#efe9d8] flex items-center justify-center px-4 py-10">
        <style>{`
          @keyframes gz-pop {
            0%   { transform: scale(0.6); opacity: 0; }
            55%  { transform: scale(1.08); opacity: 1; }
            100% { transform: scale(1); opacity: 1; }
          }
          @keyframes gz-fade-up {
            0%   { transform: translateY(14px); opacity: 0; }
            100% { transform: translateY(0);   opacity: 1; }
          }
          @keyframes gz-ring {
            0%   { transform: scale(0.9); opacity: 0.65; }
            100% { transform: scale(1.6); opacity: 0; }
          }
          .gz-pop { animation: gz-pop 0.6s cubic-bezier(0.2, 0.9, 0.3, 1.2) both; }
          .gz-fade-1 { animation: gz-fade-up 0.6s ease-out 0.25s both; }
          .gz-fade-2 { animation: gz-fade-up 0.6s ease-out 0.45s both; }
          .gz-ring { animation: gz-ring 1.4s ease-out infinite; }
        `}</style>

        <div className="w-full max-w-lg bg-white rounded-3xl shadow-2xl p-8 sm:p-10 border border-black/5 text-center">
          <div className="relative mx-auto w-24 h-24 mb-6">
            <div className="absolute inset-0 rounded-full bg-emerald-200/70 gz-ring"></div>
            <div className="absolute inset-0 rounded-full bg-emerald-500 gz-pop flex items-center justify-center shadow-lg">
              <svg viewBox="0 0 24 24" className="w-12 h-12 text-white" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
          </div>

          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 gz-fade-1">
            Online store created 🎉
          </h1>
          <p className="mt-2 text-sm text-gray-600 gz-fade-1">
            <span className="font-semibold text-[#9B4819]">{formData.shopName}</span> is now live on Campus GOSTOREz.
          </p>

          <div className="mt-8 space-y-3 gz-fade-2">

                       <button
              onClick={() => navigate(`/vendor/add-product`)}
              className="w-full py-3.5 px-4 rounded-xl text-sm font-semibold text-white bg-[#9B4819] hover:bg-[#7a3914] transition shadow-sm"
            >
              Post Your First Product or Service
            </button>

            <button
              onClick={() => navigate(`/vendor/${createdShopId}`)}
              className="w-full py-3.5 px-4 rounded-xl text-sm font-semibold text-white bg-[#9B4819] hover:bg-[#7a3914] transition shadow-sm"
            >
              Visit Shop
            </button>

            <button
              onClick={() => navigate('/vendor/dashboard')}
              className="w-full py-3.5 px-4 rounded-xl text-sm font-semibold text-[#9B4819] bg-amber-50 hover:bg-amber-100 transition border border-amber-200"
            >
              Visit Studio
            </button>

            <button
              onClick={() => navigate('/market')}
              className="w-full py-3.5 px-4 rounded-xl text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition"
            >
              Return to Market
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // MAIN WIZARD
  // ============================================================
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F4F1E8] to-[#efe9d8] py-8 sm:py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Top brand */}
        <div className="flex justify-center mb-4">
          <img src={logo} alt="Campus GOSTOREz" className="h-20 w-auto" />
        </div>

        {/* Hero heading */}
        <div className="text-center mb-6 px-2">
          <h1 className="text-2xl sm:text-3xl font-bold text-[#9B4819] leading-tight">
            Set up your business online today
          </h1>
          <p className="mt-2 text-sm sm:text-base text-gray-600 max-w-md mx-auto">
            Become visible to <span className="font-semibold text-[#9B4819]">100k+ registered potential customers</span> and start selling in minutes.
          </p>
        </div>

        {/* Progress card */}
        <div className="bg-white/80 backdrop-blur rounded-2xl border border-black/5 shadow-sm p-5">
          <div className="flex items-center justify-between text-xs font-medium text-gray-600">
            <span>Step {currentStepIndex + 1} of {totalSteps}</span>
            <span>{progressPercent}%</span>
          </div>
          <div className="mt-3 w-full bg-gray-200 rounded-full h-2 overflow-hidden">
            <div
              className="h-2 rounded-full bg-gradient-to-r from-[#9B4819] to-[#c56b3a] transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <p className="mt-3 text-xs text-gray-500 text-center">
            {userShopCount.maxAllowed - userShopCount.currentCount} shop
            {userShopCount.maxAllowed - userShopCount.currentCount !== 1 ? 's' : ''} remaining
          </p>
        </div>

        {/* Step card */}
        <div className="mt-6 bg-white rounded-3xl shadow-xl border border-black/5 p-6 sm:p-8">
          <div className="text-center mb-6">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
              {currentStep.title}
            </h2>
            {currentStep.subtitle && (
              <p className="mt-1.5 text-sm text-gray-500">
                {currentStep.subtitle}
              </p>
            )}
          </div>

          {currentStep.key === 'shopName' && (
            <input
              type="text"
              name="shopName"
              value={formData.shopName}
              onChange={handleChange}
              placeholder="e.g. Prestige Prints"
              autoFocus
              className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-[#9B4819] focus:ring-2 focus:ring-[#9B4819]/15 outline-none text-base"
            />
          )}

          {currentStep.key === 'bio' && (
            <textarea
              name="bio"
              rows={4}
              value={formData.bio}
              onChange={handleChange}
              placeholder="We sell hand-printed tote bags and custom tees designed for campus life..."
              autoFocus
              className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-[#9B4819] focus:ring-2 focus:ring-[#9B4819]/15 outline-none text-base resize-none"
            />
          )}

          {currentStep.key === 'category' && (
            <div className="space-y-3">
              {categoriesLoading && (
                <div className="flex flex-col items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9B4819]"></div>
                  <p className="mt-3 text-sm text-gray-500">Loading categories...</p>
                </div>
              )}

              {!categoriesLoading && categoriesError && (
                <div className="text-center py-6">
                  <p className="text-sm text-red-600 mb-3">{categoriesError}</p>
                  <button
                    type="button"
                    onClick={fetchCategories}
                    className="px-4 py-2 text-sm font-medium text-white bg-[#9B4819] rounded-xl hover:bg-[#7a3914] transition"
                  >
                    Try Again
                  </button>
                </div>
              )}

              {!categoriesLoading && !categoriesError && categories.length === 0 && (
                <p className="text-center text-sm text-gray-500 py-6">
                  No categories available at the moment.
                </p>
              )}

              {!categoriesLoading && !categoriesError && categories.length > 0 && (
                <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                  {categories.map((cat) => {
                    const isSelected = selectedCategoryId === cat.id;
                    return (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => setSelectedCategoryId(cat.id)}
                        className={`w-full rounded-xl border-2 px-4 py-3.5 text-left transition-all ${
                          isSelected
                            ? 'border-[#9B4819] bg-amber-50/50 shadow-sm'
                            : 'border-gray-200 hover:border-[#9B4819]/40'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <span className={`text-sm font-medium ${isSelected ? 'text-[#9B4819]' : 'text-gray-800'}`}>
                            {cat.name}
                          </span>
                          {isSelected && (
                            <div className="w-5 h-5 rounded-full bg-[#9B4819] flex items-center justify-center shrink-0">
                              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {currentStep.key === 'hashtags' && (
            <div className="space-y-4">
              <p className="text-xs text-gray-500 text-center leading-relaxed">
                Words that should display your product when user types it.
                <br />
                E.g: <span className="font-medium text-[#9B4819]">#jeansinabuja</span>
              </p>

              <div className="space-y-2">
                {hashtags.map((tag, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-gray-200 focus-within:border-[#9B4819] focus-within:ring-2 focus-within:ring-[#9B4819]/15 bg-white"
                  >
                    <span className="text-[#9B4819] font-bold text-base select-none">#</span>
                    <input
                      type="text"
                      value={tag}
                      placeholder={i === 0 ? 'jeansinabuja' : `hashtag ${i + 1}`}
                      onChange={e => handleHashtagChange(i, e.target.value)}
                      maxLength={30}
                      autoFocus={i === 0}
                      className="flex-1 outline-none text-base bg-transparent"
                    />
                  </div>
                ))}
              </div>

              <p className="text-[11px] text-gray-400 text-center">
                Up to 5 — we add the # for you. No spaces or # allowed inside the input.
              </p>
            </div>
          )}

          {currentStep.key === 'location' && (
            <div className="space-y-3">
              <select
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
                className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-[#9B4819] focus:ring-2 focus:ring-[#9B4819]/15 outline-none text-base bg-white"
              >
                <option value="">Select your state</option>
                {NIGERIAN_STATES.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <input
                type="text"
                value={addressInput}
                onChange={(e) => setAddressInput(e.target.value)}
                placeholder="Specific address — e.g. Shop 12, Faculty of Arts"
                className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-[#9B4819] focus:ring-2 focus:ring-[#9B4819]/15 outline-none text-base"
              />
              <p className="text-xs text-gray-500">
                We will save it as: <span className="font-medium text-gray-700">
                  {addressInput ? `${addressInput}, ` : 'specific address, '}
                  {selectedState || 'State'}
                </span>
              </p>
            </div>
          )}

          {currentStep.key === 'contactPhone' && (
            <input
              type="tel"
              name="contactPhone"
              value={formData.contactPhone}
              onChange={handleChange}
              placeholder="e.g. 0803 000 0000"
              autoFocus
              className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-[#9B4819] focus:ring-2 focus:ring-[#9B4819]/15 outline-none text-base"
            />
          )}

          {currentStep.key === 'businessEmail' && (
            <input
              type="email"
              name="businessEmail"
              value={formData.businessEmail}
              onChange={handleChange}
              placeholder="business@example.com"
              autoFocus
              className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-[#9B4819] focus:ring-2 focus:ring-[#9B4819]/15 outline-none text-base"
            />
          )}

          {currentStep.key === 'businessHours' && (
            <input
              type="text"
              name="businessHours"
              value={formData.businessHours}
              onChange={handleChange}
              placeholder="e.g. Mon–Fri, 9AM–6PM"
              autoFocus
              className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-[#9B4819] focus:ring-2 focus:ring-[#9B4819]/15 outline-none text-base"
            />
          )}

          {currentStep.key === 'website' && (
            <input
              type="url"
              name="website"
              value={formData.website}
              onChange={handleChange}
              placeholder="https://yourshop.com"
              autoFocus
              className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-[#9B4819] focus:ring-2 focus:ring-[#9B4819]/15 outline-none text-base"
            />
          )}

          {currentStep.key === 'instagramHandle' && (
            <input
              type="text"
              name="instagramHandle"
              value={formData.instagramHandle}
              onChange={handleChange}
              placeholder="@yourshop"
              autoFocus
              className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-[#9B4819] focus:ring-2 focus:ring-[#9B4819]/15 outline-none text-base"
            />
          )}

          {currentStep.key === 'facebookPage' && (
            <input
              type="text"
              name="facebookPage"
              value={formData.facebookPage}
              onChange={handleChange}
              placeholder="Your Shop Name"
              autoFocus
              className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-[#9B4819] focus:ring-2 focus:ring-[#9B4819]/15 outline-none text-base"
            />
          )}

          {currentStep.key === 'twitterHandle' && (
            <input
              type="text"
              name="twitterHandle"
              value={formData.twitterHandle}
              onChange={handleChange}
              placeholder="@yourshop"
              autoFocus
              className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-[#9B4819] focus:ring-2 focus:ring-[#9B4819]/15 outline-none text-base"
            />
          )}

          {currentStep.key === 'returnPolicy' && (
            <textarea
              name="returnPolicy"
              rows={3}
              value={formData.returnPolicy}
              onChange={(e) =>
                setFormData(prev => ({
                  ...prev,
                  returnPolicy: blockDigits(e.target.value)
                }))
              }
              placeholder="Payment on delivery"
              autoFocus
              className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-[#9B4819] focus:ring-2 focus:ring-[#9B4819]/15 outline-none text-base resize-none"
            />
          )}

          {currentStep.key === 'shippingPolicy' && (
            <textarea
              name="shippingPolicy"
              rows={4}
              value={formData.shippingPolicy}
              onChange={(e) =>
                setFormData(prev => ({
                  ...prev,
                  shippingPolicy: blockDigits(e.target.value)
                }))
              }
              placeholder="Receive our goods or services at our physical shop location. Contact us for more information. Thank you!"
              autoFocus
              className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-[#9B4819] focus:ring-2 focus:ring-[#9B4819]/15 outline-none text-base resize-none"
            />
          )}

          {currentStep.key === 'images' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Shop logo <span className="text-gray-400">(optional)</span>
                </p>
                <button
                  type="button"
                  onClick={() => triggerFileInput('profile-image-input')}
                  className="w-full aspect-square rounded-2xl border-2 border-dashed border-gray-200 hover:border-[#9B4819]/50 bg-gray-50 overflow-hidden flex items-center justify-center transition"
                >
                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      alt="Shop logo preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="text-center px-4">
                      <svg className="w-10 h-10 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.6">
                        <rect x="3" y="3" width="18" height="18" rx="3" />
                        <circle cx="8.5" cy="9" r="1.5" />
                        <path d="M21 15l-5-5L5 21" />
                      </svg>
                      <span className="text-xs text-gray-500">Tap to upload logo</span>
                    </div>
                  )}
                </button>
                <input
                  id="profile-image-input"
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageSelect(e, 'profile')}
                  className="hidden"
                />
              </div>

              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Cover image <span className="text-gray-400">(optional)</span>
                </p>
                <button
                  type="button"
                  onClick={() => triggerFileInput('cover-image-input')}
                  className="w-full aspect-square rounded-2xl border-2 border-dashed border-gray-200 hover:border-[#9B4819]/50 bg-gray-50 overflow-hidden flex items-center justify-center transition"
                >
                  {coverPreview ? (
                    <img
                      src={coverPreview}
                      alt="Cover preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="text-center px-4">
                      <svg className="w-10 h-10 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.6">
                        <rect x="3" y="5" width="18" height="14" rx="3" />
                        <circle cx="8" cy="10" r="1.2" />
                        <path d="M21 15l-4-3-7 7" />
                      </svg>
                      <span className="text-xs text-gray-500">Tap to upload cover</span>
                    </div>
                  )}
                </button>
                <input
                  id="cover-image-input"
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageSelect(e, 'cover')}
                  className="hidden"
                />
              </div>
            </div>
          )}

          {currentStep.key === 'review' && (
            <div className="space-y-4 text-left">
              <ReviewRow label="Shop name" value={formData.shopName} />
              <ReviewRow label="Description" value={formData.bio} />
              <ReviewRow
                label="Category"
                value={
                  categories.find(c => c.id === selectedCategoryId)?.name || '—'
                }
              />
              <ReviewRow
                label="Hashtags"
                value={
                  buildHashtagsString(hashtags) || '—'
                }
              />
              <ReviewRow label="Location" value={`${addressInput}, ${selectedState}`} />
              <ReviewRow label="Phone" value={formData.contactPhone} />
              <ReviewRow label="Email" value={formData.businessEmail || '—'} />
              <ReviewRow label="Business hours" value={formData.businessHours || '—'} />
              <ReviewRow label="Website" value={formData.website || '—'} />
              <ReviewRow label="Instagram" value={formData.instagramHandle || '—'} />
              <ReviewRow label="Facebook" value={formData.facebookPage || '—'} />
              <ReviewRow label="Twitter" value={formData.twitterHandle || '—'} />
              <ReviewRow label="Payment" value={formData.returnPolicy} />
              <ReviewRow label="Delivery" value={formData.shippingPolicy} />
              <ReviewRow label="Logo" value={profileImage ? 'Uploaded' : 'Not uploaded (will use category default)'} />
              <ReviewRow label="Cover" value={coverImage ? 'Uploaded' : 'Not uploaded (will use category default)'} />
            </div>
          )}

          {error && (
            <div className="mt-5 rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="mt-8 flex items-center gap-3">
            <button
              type="button"
              onClick={goBack}
              disabled={currentStepIndex === 0 || loading}
              className="flex-1 py-3.5 rounded-xl text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Back
            </button>

            {!currentStep.required && currentStep.key !== 'review' && currentStep.key !== 'images' && (
              <button
                type="button"
                onClick={skip}
                disabled={loading}
                className="flex-1 py-3.5 rounded-xl text-sm font-semibold text-gray-500 bg-white border border-gray-200 hover:bg-gray-50 transition disabled:opacity-40"
              >
                Skip
              </button>
            )}

            {currentStep.key !== 'review' ? (
              <button
                type="button"
                onClick={goNext}
                disabled={!canProceed || loading}
                className="flex-1 py-3.5 rounded-xl text-sm font-semibold text-white bg-[#9B4819] hover:bg-[#7a3914] transition disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Continue
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading || !canSubmit()}
                className="flex-1 py-3.5 rounded-xl text-sm font-semibold text-white bg-[#9B4819] hover:bg-[#7a3914] transition disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Creating...
                  </>
                ) : (
                  `Create Shop ${userShopCount.currentCount + 1}/${userShopCount.maxAllowed}`
                )}
              </button>
            )}
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-gray-500">
          Logged in as {currentUser.email}
        </p>
      </div>
    </div>
  );
};

const ReviewRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex items-start gap-3 border-b border-gray-100 pb-2">
    <span className="text-xs font-medium text-gray-500 w-28 shrink-0 pt-0.5">
      {label}
    </span>
    <span className="text-sm text-gray-800 break-words flex-1">
      {value}
    </span>
  </div>
);

export default VendorOnboarding;